import { useTranslateHeaders } from '@/ui/table/helpers/use-translate-headers';
import { projectSuggestionsTableHeaders } from './headers-config';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { trackMixpanelSearchResultClicked } from '@/lib/mixpanel/mixpanel';
import { colors } from '@/theme/colors';
import { paths } from '@/routes/helpers/paths';
import { modalState } from '@/state/modal.state';
import { useProjectListingsSectionParams } from '@/features/projects/project-listings/use-project-listings-section-params';
import type { ProjectSearchSuggestion } from './project-suggestions-table';
import { ProjectRowMainCell } from './project-row-main-cell';
import { CellLink } from '../enriched-table/ui';
import { SkeletonText } from '@/ui/table/infinite-table/skeleton-row';
import { ProjectActions } from './project-actions';
import { ProjectStageCell } from '@/features/projects/projects-list/components/project-stage-cell';
import { ProjectCreatedForCell } from '@/features/projects/projects-list/components/project-created-for-cell';
import { ProjectTypeTag } from '@/features/projects/projects-list/project-type-tag';
import { StatusIndicatorCell } from '@/features/projects/projects-list/components/status-indicator-cell';
import { ProjectLastModifiedCell } from '@/features/projects/projects-list/components/project-last-modified-cell';
import { ProjectOwnerCell } from '@/features/projects/projects-list/components/project-owner-cell';
import { TagsPreview } from '@/features/tags/tags-preview';
import { PROJECT_CELL_VERTICAL_PADDING } from './constants';
import { resolveResponsiveValue } from '@/theme/breakpoints';
import { useResponsiveTagsCount } from '@/features/tags/use-responsive-tags-count';
import { useSetAtom } from 'jotai';

// Add a hook to force re-render on window resize
function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1920,
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

export const useProjectSuggestionsTableConfig = (
  data: ProjectSearchSuggestion[],
  isEnrichDataLoading: boolean,
) => {
  useWindowWidth();
  const setModal = useSetAtom(modalState);
  const maxVisibleTags = useResponsiveTagsCount();

  const onProjectLinkInteract = useCallback((projectId: string) => {
    trackMixpanelSearchResultClicked({
      entity_id: projectId,
      entity_type: 'project',
      entry_surface: 'global_search_projects_table',
      is_recent: false,
    });
  }, []);
  const listingsSectionParams = useProjectListingsSectionParams();
  const headers = useTranslateHeaders(
    projectSuggestionsTableHeaders,
    'default',
    'globalSearch.suggestionsTable',
  );

  const allowedAligns = ['left', 'center', 'right'] as const;
  type AllowedAlign = (typeof allowedAligns)[number];

  const headerLayoutMap = headers.cells.reduce(
    (acc, header) => {
      acc[header.columnKey] = {
        width: resolveResponsiveValue(header.width),
        minWidth: resolveResponsiveValue(header.minWidth),
        maxWidth: resolveResponsiveValue(header.maxWidth),
        padding: PROJECT_CELL_VERTICAL_PADDING,
        align: allowedAligns.includes(header.align as AllowedAlign)
          ? (header.align as AllowedAlign)
          : undefined,
      };
      return acc;
    },
    {} as Record<
      string,
      {
        width?: string;
        minWidth?: string;
        maxWidth?: string;
        padding?: string;
        align?: AllowedAlign;
      }
    >,
  );

  const rows = useMemo(() => {
    return data.map(({ basicData, enrichedData }) => {
      const userOwnerToDisplay = enrichedData.owner ?? enrichedData.creator;
      // Define the cells in the same order as headers
      const cellData: Record<string, any> = {
        projects: (
          <CellLink
            onClick={() => onProjectLinkInteract(basicData.id)}
            to={{
              pathname: paths.project({ projectId: basicData.id }),
              search: listingsSectionParams,
            }}
          >
            <ProjectRowMainCell
              name={basicData.name}
              listingsCount={basicData.listingsCount}
              problemStatement={enrichedData.problemStatement}
              isEnrichDataLoading={isEnrichDataLoading}
            />
          </CellLink>
        ),
        type: (
          <CellLink
            onClick={() => onProjectLinkInteract(basicData.id)}
            to={{
              pathname: paths.project({ projectId: basicData.id }),
              search: listingsSectionParams,
            }}
          >
            <ProjectTypeTag text={enrichedData.type} />
          </CellLink>
        ),
        tags: (
          <CellLink
            onClick={() => onProjectLinkInteract(basicData.id)}
            to={{
              pathname: paths.project({ projectId: basicData.id }),
              search: listingsSectionParams,
            }}
          >
            <TagsPreview
              tags={enrichedData.tags}
              isLoading={isEnrichDataLoading}
              maxVisibleTags={maxVisibleTags}
            />
          </CellLink>
        ),
        stage: isEnrichDataLoading ? (
          <SkeletonText />
        ) : (
          <CellLink
            onClick={() => onProjectLinkInteract(basicData.id)}
            to={{
              pathname: paths.project({ projectId: basicData.id }),
              search: listingsSectionParams,
            }}
          >
            <ProjectStageCell stage={enrichedData.mostAdvancedListingStatus} />
          </CellLink>
        ),
        createdFor: (
          <CellLink
            onClick={() => onProjectLinkInteract(basicData.id)}
            to={{
              pathname: paths.project({ projectId: basicData.id }),
              search: listingsSectionParams,
            }}
          >
            <ProjectCreatedForCell
              createdFor={enrichedData.createdForTeam}
              disableLink
            />
          </CellLink>
        ),
        status: isEnrichDataLoading ? (
          <SkeletonText />
        ) : (
          <CellLink
            onClick={() => onProjectLinkInteract(basicData.id)}
            to={{
              pathname: paths.project({ projectId: basicData.id }),
              search: listingsSectionParams,
            }}
          >
            {enrichedData.status ? (
              <StatusIndicatorCell status={enrichedData.status} />
            ) : null}
          </CellLink>
        ),
        lastModified: (
          <CellLink
            onClick={() => onProjectLinkInteract(basicData.id)}
            to={{
              pathname: paths.project({ projectId: basicData.id }),
              search: listingsSectionParams,
            }}
          >
            <ProjectLastModifiedCell
              lastEditedDate={enrichedData.lastEditedDate}
            />
          </CellLink>
        ),
        projectOwner: (
          <CellLink
            onClick={() => onProjectLinkInteract(basicData.id)}
            to={{
              pathname: paths.project({ projectId: basicData.id }),
              search: listingsSectionParams,
            }}
          >
            <ProjectOwnerCell
              owner={`${userOwnerToDisplay.firstName} ${userOwnerToDisplay.lastName}`}
            />
          </CellLink>
        ),
        actions: <ProjectActions projectId={basicData.id} />,
      };

      // Build cells array in header order, syncing layout props
      const cells = headers.cells.map(header => {
        // Special case: actions column is hoverOnly and not in headers
        if (header.columnKey === 'actions') {
          return {
            value: cellData.actions,
            hoverOnly: true,
          };
        }
        return {
          value: cellData[header.columnKey],
          ...headerLayoutMap[header.columnKey],
        };
      });

      // Add actions cell at the end if not in headers
      if (!headers.cells.some(h => h.columnKey === 'actions')) {
        cells.push({ value: cellData.actions, hoverOnly: true });
      }

      return {
        id: basicData.id,
        rowHover: true,
        rowBackground: colors.basics.white,
        rowBackgroundHover: colors.gray.c1,
        onClick: () => setModal(null),
        cells,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    isEnrichDataLoading,
    headers,
    maxVisibleTags,
    onProjectLinkInteract,
  ]);

  return {
    headers,
    rows,
  };
};
